import { useCallback, useEffect, useRef, useState } from "react";
import { getBinding } from "../../lib/binding";
import type {
  CareWhiteboardProjection,
  RealtimeEvent,
} from "@shared/types/foyer";

/**
 * Fetches the in-room care whiteboard projection for the paired room and keeps
 * it fresh. Mirrors {@link useRoomPhotos}:
 *   - fetch on mount (when `active`),
 *   - poll every 30 s while active (dedicated device),
 *   - re-fetch on a `care_profile_changed` realtime event.
 *
 * The projection carries no PHI identifiers (only the already-reduced display
 * name), so the read is a plain screen-token GET. `previewPremiseId` /
 * `previewRoomId` drive the CMS device-preview path (no real binding).
 */
export function useRoomWhiteboard(
  active: boolean,
  realtimeEvent?: RealtimeEvent | null,
  previewPremiseId?: string,
  previewRoomId?: string
): {
  projection: CareWhiteboardProjection | null;
  loading: boolean;
  fetchError: string | null;
} {
  const [projection, setProjection] = useState<CareWhiteboardProjection | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchWhiteboard = useCallback(async () => {
    const binding = getBinding();

    // CMS preview mode - no device binding.
    if (!binding && previewPremiseId) {
      if (!previewRoomId) return; // generic preview: no room context.
      setLoading(true);
      try {
        const res = await fetch(
          `/api/foyer/premise/${previewPremiseId}/rooms/${encodeURIComponent(
            previewRoomId
          )}/care/whiteboard`
        );
        if (res.ok) {
          setProjection((await res.json()) as CareWhiteboardProjection);
        }
      } catch {
        // preview is best-effort
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!binding) return;
    setLoading(true);
    try {
      const headers: Record<string, string> = {};
      if (binding.screenToken) headers["X-Screen-Token"] = binding.screenToken;
      const res = await fetch(
        `/api/foyer/premise/${binding.premiseId}/rooms/${binding.roomId}/care/whiteboard`,
        { headers }
      );
      if (res.ok) {
        setFetchError(null);
        setProjection((await res.json()) as CareWhiteboardProjection);
      } else {
        const body = await res.text().catch(() => "");
        setFetchError(`HTTP ${res.status} - ${body.slice(0, 120)}`);
      }
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [previewPremiseId, previewRoomId]);

  useEffect(() => {
    if (!active) return;
    void fetchWhiteboard();
    intervalRef.current = setInterval(fetchWhiteboard, 30_000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [active, fetchWhiteboard]);

  useEffect(() => {
    if (!realtimeEvent || realtimeEvent.type !== "care_profile_changed") return;
    void fetchWhiteboard();
  }, [realtimeEvent, fetchWhiteboard]);

  return { projection, loading, fetchError };
}
